import React, { useContext } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  styled,
  useTheme,
  CustomTheme,
  Stack,
  Divider,
  ListItemIcon,
  ListItemText,
  IconButton,
  Link,
  LinkProps,
} from "@mui/material";
import { useLocation } from "react-router";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import { LOGOUT } from "graphql/UserQueries";
import { useMutation, useQuery } from "@apollo/client";
import { navigateToExternalURL } from "shared/utils";
import UserContext from "contexts/UserContext";
import EnvironmentBanner from "./EnvironmentBanner";
import Favicon from "react-favicon";
import { USER_QUERY } from "graphql/UserQueries";
import { AccountCircle } from "@mui/icons-material";

interface TopNavLinkProps extends LinkProps {
  text: string;
  isActive: boolean;
}

const TopNavLink = ({ text, isActive, ...rest }: TopNavLinkProps) => {
  return (
    <Stack
      paddingY={1.5}
      paddingX={1}
      borderBottom={2}
      borderColor={isActive ? "primary.contrastText" : "transparent"}
      alignItems="center"
      marginBottom="-6px"
    >
      <Link
        underline="none"
        color="inherit"
        variant="subtitle2"
        style={{ display: "inline-flex" }}
        marginBottom="2px"
        data-testid={`${text.toLowerCase()}-link`}
        {...rest}
      >
        {text.toUpperCase()}
      </Link>
    </Stack>
  );
};

const TopNav = (): React.ReactElement => {
  const location = useLocation();
  const theme: CustomTheme = useTheme();
  const [anchorDropDown, setAnchorDropDown] =
    React.useState<null | HTMLElement>(null);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorDropDown(event.currentTarget);
  };
  const isActiveItem = (itemLocation: string): boolean => {
    return location.pathname.startsWith(itemLocation);
  };
  const isMenuOpen = Boolean(anchorDropDown);
  const handleMenuClose = () => {
    setAnchorDropDown(null);
  };

  const user = useContext(UserContext);

  const [logout] = useMutation(LOGOUT);

  const { data } = useQuery(USER_QUERY);

  const renderMenu = (
    <Menu
      anchorEl={anchorDropDown}
      id="account-menu"
      open={isMenuOpen}
      onClose={handleMenuClose}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            width: "200px",
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
    >
      <MenuItem>
        <ListItemText>
          {user.firstName} {user.lastName}
        </ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem
        onClick={() => {
          navigateToExternalURL("sso/logout");
          logout();
        }}
      >
        <ListItemIcon>
          <ExitToAppOutlinedIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Logout</ListItemText>
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar position="static" sx={{ zIndex: 1030, height: "56px" }}>
      <Favicon url={theme.custom.favicon} />
      <Toolbar
        variant="dense"
        style={{ backgroundColor: theme.palette.primary.main }}
        disableGutters
        sx={{
          paddingLeft: "20px",
          paddingRight: "16px",
        }}
      >
        <Link
          href="/search"
          underline="none"
          color="inherit"
          variant="h6"
          data-testid="app-icon"
          sx={{
            height: "43px",
            display: "inline-flex",
            svg: {
              height: "43px",
              width: "auto",
            },
          }}
        >
          {theme?.custom?.logo}
        </Link>
        <Stack
          direction="row"
          gap={2}
          width={1}
          alignItems="center"
          marginLeft="80px"
          height={"56px"}
        >
          {data?.user?.roAccess && (
            <TopNavLink
              href="/search"
              text={user ? "Search" : "Login"}
              isActive={isActiveItem("/search")}
            />
          )}
          {data?.user?.roAccess && (
            <TopNavLink
              href="/review"
              data-testid="review-link"
              text="Review"
              isActive={isActiveItem("/review")}
            />
          )}
          {data?.user?.moAccess && (
            <TopNavLink
              href="/protocol_search"
              text="Protocol Templates"
              isActive={isActiveItem("/protocol_search")}
            />
          )}
          {data?.user?.moAccess && (
            <TopNavLink
              href="/formulary"
              data-testid="formulary-link"
              text="Formulary"
              isActive={isActiveItem("/formulary")}
            />
          )}
          {user.recipeManagerAccess && (
            <TopNavLink
              href="/blueprint"
              text="Blueprints"
              isActive={isActiveItem("/blueprint")}
            />
          )}
        </Stack>
        <EnvironmentBanner />
        {user && (
          <IconButton onClick={handleProfileMenuOpen}>
            <AccountCircle sx={{ color: "primary.contrastText" }} />
          </IconButton>
        )}
      </Toolbar>
      {renderMenu}
    </AppBar>
  );
};

export default TopNav;
